import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: "owner" | "admin" | "member";
  joined_at: string;
  profile?: {
    email: string | null;
    full_name: string | null;
    avatar_url: string | null;
  };
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: "admin" | "member";
  expires_at: string;
  created_at: string;
}

export interface CreateOrgData {
  name: string;
  slug: string;
  logo_url?: string;
}

export function useOrganization() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [userRole, setUserRole] = useState<"owner" | "admin" | "member" | null>(null);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);

  const fetchOrganization = useCallback(async () => {
    if (!user) {
      setOrganization(null);
      setUserRole(null);
      setLoading(false);
      return;
    }

    try {
      // Get user's organization membership
      const { data: membershipData, error: membershipError } = await supabase
        .from("organization_members")
        .select("organization_id, role")
        .eq("user_id", user.id)
        .single();

      if (membershipError) {
        if (membershipError.code === "PGRST116") {
          // No organization found - that's okay
          setOrganization(null);
          setUserRole(null);
          setLoading(false);
          return;
        }
        throw membershipError;
      }

      setUserRole(membershipData.role as "owner" | "admin" | "member");

      // Get organization details
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", membershipData.organization_id)
        .single();

      if (orgError) throw orgError;

      setOrganization(orgData);
    } catch (error) {
      console.error("Failed to fetch organization:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchMembers = useCallback(async () => {
    if (!organization) {
      setMembers([]);
      return;
    }

    setMembersLoading(true);
    try {
      // Get members
      const { data: membersData, error: membersError } = await supabase
        .from("organization_members")
        .select("*")
        .eq("organization_id", organization.id)
        .order("joined_at", { ascending: true });

      if (membersError) throw membersError;

      // Get profiles for all members
      const userIds = membersData.map((m) => m.user_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, email, full_name, avatar_url")
        .in("user_id", userIds);

      if (profilesError) throw profilesError;

      // Combine members with profiles
      const membersWithProfiles = membersData.map((member) => ({
        ...member,
        role: member.role as "owner" | "admin" | "member",
        profile: profilesData?.find((p) => p.user_id === member.user_id) || undefined,
      }));

      setMembers(membersWithProfiles);
    } catch (error) {
      console.error("Failed to fetch members:", error);
    } finally {
      setMembersLoading(false);
    }
  }, [organization]);

  const createOrganization = useCallback(async (data: CreateOrgData) => {
    if (!user) return null;

    try {
      // Create organization - the database trigger automatically adds creator as owner
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .insert({
          name: data.name,
          slug: data.slug.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
          logo_url: data.logo_url || null,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      toast({
        title: "Organization created",
        description: `${data.name} has been created successfully.`,
      });

      await fetchOrganization();
      return orgData;
    } catch (error: any) {
      console.error("Failed to create organization:", error);
      toast({
        title: "Failed to create organization",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return null;
    }
  }, [user, fetchOrganization, toast]);

  const updateOrganization = useCallback(async (updates: Partial<CreateOrgData>) => {
    const canUpdate = userRole === "owner" || userRole === "admin";
    if (!organization || !canUpdate) return false;

    try {
      const { error } = await supabase
        .from("organizations")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", organization.id);

      if (error) throw error;

      await fetchOrganization();
      
      toast({
        title: "Organization updated",
        description: "Organization settings have been saved.",
      });
      
      return true;
    } catch (error: any) {
      console.error("Failed to update organization:", error);
      toast({
        title: "Update failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [organization, userRole, fetchOrganization, toast]);

  const inviteMember = useCallback(async (email: string, role: "admin" | "member" = "member") => {
    const canInvite = userRole === "owner" || userRole === "admin";
    if (!organization || !canInvite) return false;

    try {
      // Use edge function to send email invitation
      const { data, error } = await supabase.functions.invoke("invite-member", {
        body: {
          email,
          role,
          organizationId: organization.id,
        },
      });

      if (error) throw error;

      if (data.error) {
        toast({
          title: "Invitation failed",
          description: data.error,
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${email}.`,
      });

      await fetchMembers();
      return true;
    } catch (error: any) {
      console.error("Failed to invite member:", error);
      toast({
        title: "Failed to send invitation",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [organization, userRole, fetchMembers, toast]);

  const updateMemberRole = useCallback(async (memberId: string, role: "admin" | "member") => {
    const canUpdate = userRole === "owner" || userRole === "admin";
    if (!organization || !canUpdate) return false;

    try {
      const { error } = await supabase
        .from("organization_members")
        .update({ role })
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: "Role updated",
        description: "Member role has been updated.",
      });

      await fetchMembers();
      return true;
    } catch (error: any) {
      console.error("Failed to update member role:", error);
      toast({
        title: "Update failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [organization, userRole, fetchMembers, toast]);

  const removeMember = useCallback(async (memberId: string) => {
    const canRemove = userRole === "owner" || userRole === "admin";
    if (!organization || !canRemove) return false;

    // Can't remove owner
    const member = members.find((m) => m.id === memberId);
    if (member?.role === "owner") {
      toast({
        title: "Cannot remove owner",
        description: "The organization owner cannot be removed.",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from("organization_members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: "Member removed",
        description: "Member has been removed from the organization.",
      });

      await fetchMembers();
      return true;
    } catch (error: any) {
      console.error("Failed to remove member:", error);
      toast({
        title: "Remove failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [organization, userRole, members, fetchMembers, toast]);

  const leaveOrganization = useCallback(async () => {
    if (!organization || !user) return false;

    // Owner can't leave
    if (userRole === "owner") {
      toast({
        title: "Cannot leave",
        description: "You must transfer ownership before leaving.",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from("organization_members")
        .delete()
        .eq("organization_id", organization.id)
        .eq("user_id", user.id);

      if (error) throw error;

      toast({
        title: "Left organization",
        description: `You have left ${organization.name}.`,
      });

      setOrganization(null);
      setUserRole(null);
      setMembers([]);
      return true;
    } catch (error: any) {
      console.error("Failed to leave organization:", error);
      toast({
        title: "Failed to leave",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [organization, user, userRole, toast]);

  const acceptInvitation = useCallback(async (token: string) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase.functions.invoke("accept-invite", {
        body: { token },
      });

      if (error) throw error;

      if (data.error) {
        toast({
          title: "Invitation failed",
          description: data.error,
          variant: "destructive",
        });
        return null;
      }

      toast({
        title: "Welcome!",
        description: `You've joined ${data.organization_name}.`,
      });

      await fetchOrganization();
      return data;
    } catch (error: any) {
      console.error("Failed to accept invitation:", error);
      toast({
        title: "Failed to accept invitation",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return null;
    }
  }, [user, fetchOrganization, toast]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    if (organization) {
      fetchMembers();
    }
  }, [organization, fetchMembers]);

  const isAdmin = userRole === "owner" || userRole === "admin";
  const isOwner = userRole === "owner";

  return {
    organization,
    members,
    userRole,
    isAdmin,
    isOwner,
    loading,
    membersLoading,
    createOrganization,
    updateOrganization,
    inviteMember,
    updateMemberRole,
    removeMember,
    leaveOrganization,
    acceptInvitation,
    refetch: fetchOrganization,
    refetchMembers: fetchMembers,
  };
}
